import IOSScroll from '@common/components/IOSScroll';
import useOverflow from '@common/hooks/useOverflow';
import Category from '@common/models/Category';
import SectionHeading from '@components/SectionHeading';
import LChevron1 from '@images/LChevron1.svg';
import Image from '@lib/Image';
import Link from '@lib/Link';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { isBrowser, isIOSDevice, isMobile } from '../../../common/utils';

interface FeaturedCategoriesProps {
  categories: Category[];
  heading: string;
  id: number;
}

const FeaturedCategories: React.FC<FeaturedCategoriesProps> = ({ categories, heading, id }) => {
  const scrollRef = useRef(null);
  const [hover, setHover] = useState(null);

  const { xOverflowing } = useOverflow(scrollRef);

  const [leftArrow, setLeftArrow] = useState(false);
  const [rightArrow, setRightArrow] = useState(true);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    setRightArrow(scrollRef.current.scrollWidth > window.innerWidth);
  }, []);

  return (
    <div className="relative cs-23 category-showcase-container layout-container mx-auto py-40 overflow-x-hidden">
      <div
        id="category"
        className={classnames('container flex  items-center mb-40 md:mb-48  mx-auto w-full', {
          'justify-center': !xOverflowing && categories.length <= 3,
          'justify-between': categories.length > 3 || xOverflowing,
        })}
      >
        <SectionHeading>{heading}</SectionHeading>
        <div>
          {xOverflowing ? (
            <div className="w-full flex">
              <button
                className={classnames('focus:outline-none mr-12', {
                  'opacity-70 cursor-not-allowed pointer-events-none': !leftArrow,
                })}
                onClick={() => {
                  document.getElementById(`categoryScroll-${id}`).scrollBy({
                    top: 0,
                    left: isMobile ? -300 : -1250,
                    behavior: 'smooth',
                  });
                }}
              >
                <img src={LChevron1} className="fill-current" />
              </button>
              <button
                className={classnames('focus:outline-none', {
                  'opacity-70 cursor-not-allowed pointer-events-none': !rightArrow,
                })}
                onClick={() => {
                  document.getElementById(`categoryScroll-${id}`).scrollBy({
                    top: 0,
                    left: isMobile ? +300 : +1250,
                    behavior: 'smooth',
                  });
                }}
              >
                <img src={LChevron1} className="fill-current transform -rotate-180" />
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <div
        className={classnames('', {
          ' relative': !(isBrowser() && isIOSDevice()),
        })}
      >
        <div className="relative">
          <div
            className={classnames('flex flex-no-wrap gap-10 md:gap-16 overflow-x-auto', {
              'justify-center': categories.length <= 3 && !xOverflowing,
              'custom-scrollbar pb-36 md:pb-52': xOverflowing,
              'ios-scroll': isBrowser() && isIOSDevice() && xOverflowing,
            })}
            ref={scrollRef}
            id={`categoryScroll-${id}`}
            onScroll={() => {
              const ele = document.getElementById(`categoryScroll-${id}`);
              let val = 0;
              setScrollLeft(ele.scrollLeft);
              if (isMobile()) val = 100;
              else val = 0;
              if (ele.scrollLeft > val) setLeftArrow(true);
              else setLeftArrow(false);
              ele.scrollWidth - ele.scrollLeft <= ele.clientWidth ? setRightArrow(false) : setRightArrow(true);
            }}
          >
            {categories?.map((category) => {
              return (
                <div
                  key={category.id}
                  className={classnames(
                    'relative category-card flex-shrink-0 w-130 md:w-270 h-130 md:h-270 bg-overlayBg',
                    {
                      'add-margin-right': xOverflowing,
                      'add-margin-left': categories.length > 3 || xOverflowing,
                    },
                  )}
                  onMouseOver={() => setHover(category.id)}
                  onMouseLeave={() => setHover(null)}
                >
                  <Link to={category.storeLink}>
                    <div className="w-full h-full overflow-hidden category-img category-showcase-transition">
                      <Image
                        className="object-cover h-full w-full category-showcase-transition"
                        src={category.homePageLambdaImage}
                        alt={category.name}
                        noDefaultDimensions
                        noDefaultFit
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 bg-overlayBg w-full h-full"></div>
                    <div className="w-full absolute bottom-0 p-10 md:p-16">
                      <h3
                        className={classnames('text-left text-white text-13 md:text-16 add-ellipsis break-word-only', {
                          'font-normal': hover !== category.id,
                          'font-bold': hover == category.id,
                        })}
                        font-customisation="section-heading"
                        color-customisation="page-heading"
                      >
                        {category.name}
                      </h3>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
          {xOverflowing && isBrowser() && isIOSDevice() ? (
            <IOSScroll scrollRef={scrollRef} scrollLeft={scrollLeft} width={isMobile() ? 70 : 172} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FeaturedCategories;
